import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import {
  getCompletedConsultationSummaryApi,
  getHospitalSummaryApi,
  getPatientsSummaryApi,
} from "@/utils/apis/doctor/dashboard";
import { getDoctorHospitalListApi } from "@/utils/apis/doctor/ipDashboard";

import {
  IDashboardInitialState,
  IDashboardQueryParams,
} from "@/models/dashboard";
import { GBSCError } from "@/models/GBSCError";
import { IDoctorHospitalListResponse } from "@/models/ApiRequestResponse/doctor";

import { RootState } from "@/store/store";

export const fetchPatientSummaryDetail = createAsyncThunk(
  "fetch-patient-summary",
  async ({ startDate, endDate, bookedBy, isExport }: IDashboardQueryParams) => {
    const response = await getPatientsSummaryApi({
      startDate,
      endDate,
      bookedBy,
      isExport,
    });
    return response;
  }
);

export const fetchIpAnalyticsDetail = createAsyncThunk(
  "fetch-ip-analytics",
  async ({ startDate, endDate, hospitalId }: IDashboardQueryParams) => {
    const response = await getHospitalSummaryApi({
      startDate,
      endDate,
      hospitalId,
    });

    return response;
  }
);

export const fetchDoctorHospitalListing = createAsyncThunk<
  IDoctorHospitalListResponse | GBSCError
>("doctor/fetchDoctorHospitalListing", async () => {
  const response = await getDoctorHospitalListApi();
  return response;
});

export const fetchCompletedConsultationSummaryDetail = createAsyncThunk(
  "fetch-completed-consultation",
  async ({ startDate, endDate, isExport, bookedBy }: IDashboardQueryParams) => {
    const response = await getCompletedConsultationSummaryApi({
      startDate,
      endDate,
      bookedBy,
      isExport,
    });
    return response;
  }
);

const dashboardSlice = createSlice({
  name: "medicalRecords",
  initialState: {
    patientSummary: {
      isFetchPatientSummaryLoading: true,
      detail: {},
    },
    ipAnalytics: {
      isFetchIpAnalyticsLoading: true,
      chartData: [],
      isHospitalsLoading: false,
      hospitals: [],
    },
    completedConsultationSummary: {
      isFetchCompletedConsultationSummaryLoading: true,
      detail: {},
    },
  } as IDashboardInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatientSummaryDetail.pending, (state) => {
        state.patientSummary.isFetchPatientSummaryLoading = true;
      })
      .addCase(fetchPatientSummaryDetail.fulfilled, (state, action) => {
        state.patientSummary.isFetchPatientSummaryLoading = false;
        if ("result" in action.payload) {
          const exportResponse = action.payload as unknown as {
            result: { url: string };
          };

          if (exportResponse.result.url) {
            window.location.href = exportResponse.result.url;
          } else {
            state.patientSummary.detail = action.payload.result;
          }
        }
      })
      .addCase(fetchPatientSummaryDetail.rejected, (state) => {
        state.patientSummary.isFetchPatientSummaryLoading = false;
      })
      .addCase(fetchIpAnalyticsDetail.pending, (state) => {
        state.ipAnalytics.isFetchIpAnalyticsLoading = true;
      })
      .addCase(fetchIpAnalyticsDetail.fulfilled, (state, action) => {
        state.ipAnalytics.isFetchIpAnalyticsLoading = false;
        if ("result" in action.payload) {
          state.ipAnalytics.chartData = action.payload.result;
        }
      })
      .addCase(fetchIpAnalyticsDetail.rejected, (state) => {
        state.ipAnalytics.isHospitalsLoading = false;
      })
      .addCase(fetchCompletedConsultationSummaryDetail.pending, (state) => {
        state.completedConsultationSummary.isFetchCompletedConsultationSummaryLoading =
          true;
      })
      .addCase(
        fetchCompletedConsultationSummaryDetail.fulfilled,
        (state, action) => {
          state.completedConsultationSummary.isFetchCompletedConsultationSummaryLoading =
            false;
          if ("result" in action.payload) {
            const exportResponse = action.payload as unknown as {
              result: { url: string };
            };

            if (exportResponse.result.url) {
              window.location.href = exportResponse.result.url;
            } else {
              state.completedConsultationSummary.detail = action.payload.result;
            }
          }
        }
      )
      .addCase(fetchCompletedConsultationSummaryDetail.rejected, (state) => {
        state.completedConsultationSummary.isFetchCompletedConsultationSummaryLoading =
          false;
      })
      .addCase(fetchDoctorHospitalListing.pending, (state) => {
        state.ipAnalytics.isHospitalsLoading = true;
      })
      .addCase(fetchDoctorHospitalListing.fulfilled, (state, action) => {
        state.ipAnalytics.isHospitalsLoading = false;
        if ("result" in action.payload) {
          state.ipAnalytics.hospitals = action.payload.result;
        }
      })
      .addCase(fetchDoctorHospitalListing.rejected, (state) => {
        state.ipAnalytics.isHospitalsLoading = false;
      });
  },
});

const selectDashboard = (state: RootState) => state.dashboard;

export const dashboard = createSelector(
  [selectDashboard],
  (selectDashboard) => {
    return {
      isFetchPatientSummaryLoading:
        selectDashboard.patientSummary.isFetchPatientSummaryLoading,
      patientSummaryDetail: selectDashboard.patientSummary.detail,
      isFetchIpAnalyticsLoading:
        selectDashboard.ipAnalytics.isFetchIpAnalyticsLoading,
      chartData: selectDashboard.ipAnalytics.chartData,
      isHospitalsLoading: selectDashboard.ipAnalytics.isHospitalsLoading,
      hospitals: selectDashboard.ipAnalytics.hospitals,
      isFetchCompletedConsultationSummaryLoading:
        selectDashboard.completedConsultationSummary
          .isFetchCompletedConsultationSummaryLoading,
      completedConsultationSummaryDetail:
        selectDashboard.completedConsultationSummary.detail,
    };
  }
);

export const {} = dashboardSlice.actions;

export default dashboardSlice.reducer;
